import React, { useState, useEffect } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { 
  Upload, 
  FileText, 
  BarChart3, 
  CheckCircle, 
  AlertTriangle, 
  XCircle,
  Download,
  RefreshCw,
  Eye,
  Filter,
  Search,
  FileSpreadsheet,
  Trash2,
  Archive,
  ArchiveRestore
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import {
  getSalesDataFiles,
  getSalesDataStatistics,
  importSalesData,
  downloadSalesDataTemplate,
  deleteSalesDataFile,
  archiveSalesDataFile,
  unarchiveSalesDataFile,
  downloadExport
} from '../../config/api';
import SalesDataFileModal from '../../components/SalesDataFileModal';
import SalesRecordsTable from './SalesRecordsTable';
import ColumnMappingModal from '../../components/SalesData/ColumnMappingModal';
import PillTabs from '../../components/ui/PillTabs';
import { useTableSort, SortTh } from '../../components/ui/tableSort';

// Column -> value accessor for client-side sorting of the file list.
const SALES_FILE_SORTS = {
  file: (f) => f.originalFileName,
  status: (f) => f.status,
  records: (f) => (f.totalRecords == null ? null : Number(f.totalRecords)),
  uploaded: (f) => (f.uploadDate ? new Date(f.uploadDate).getTime() : null),
};

const SalesDataDashboard = () => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [viewingFileId, setViewingFileId] = useState(null);
  const [showFileModal, setShowFileModal] = useState(false);
  const [tab, setTab] = useState('records'); // 'records' (line items) | 'files'
  const [showMappingModal, setShowMappingModal] = useState(false);
  const [importOptions, setImportOptions] = useState({
    validateData: true,
    detectDuplicates: true,
    matchCustomers: true,
    matchProducts: true,
    matchContracts: true
  });
  const [filters, setFilters] = useState({
    status: '',
    archived: '', // '' = active only, 'true' = archived only
    page: 1,
    limit: 10
  });

  // Export sales data to Excel
  const exportToExcel = async () => {
    try {
      await downloadExport('/api/sales-data/export', `sales-data-export-${new Date().toISOString().split('T')[0]}.xlsx`);
      toast.success('Sales data exported to Excel successfully!');
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Failed to export sales data to Excel');
    }
  };

  // Fetch sales data files
  const { data: filesData, isLoading: filesLoading, refetch: refetchFiles } = useQuery({
    queryKey: ['salesDataFiles', filters],
    queryFn: () => getSalesDataFiles(filters),
    
      refetchInterval: 30000, // Refresh every 30 seconds
    
  });

  // Fetch sales data statistics
  const { data: statsData, isLoading: statsLoading, refetch: refetchStats } = useQuery({
    queryKey: ['salesDataStatistics'],
    queryFn: () => getSalesDataStatistics(),
    
      refetchInterval: 30000,
    
  });

  // Import sales data mutation
  const importMutation = useMutation({
    mutationFn: ({ file, options }) => importSalesData(file, options),
    onSuccess: (data) => {
      const r = data?.data || {};
      const imported = r.processedRecords ?? 0;
      const total = r.totalRecords ?? imported;
      toast.success(`Imported ${imported.toLocaleString()} of ${total.toLocaleString()} sales records`);

      // Warn (don't fail) when rows saved but didn't link to an existing
      // Customer/Product/Contract — the usual cause is importing Sales Data
      // before those records exist. The rows are kept but won't match/price
      // until the dependency is added and the file is re-imported.
      const uc = r.unmatchedCustomers || 0, up = r.unmatchedProducts || 0, uk = r.unmatchedContracts || 0;
      if (uc || up || uk) {
        const parts = [];
        if (up) parts.push(`${up} with no matching product`);
        if (uc) parts.push(`${uc} with no matching customer`);
        if (uk) parts.push(`${uk} with no matching contract`);
        toast(
          `Heads up: ${parts.join(', ')}. Import your Products, Customers, and Contracts first, then re-import this file so the rows link up (unlinked rows won’t match to claims or feed pricing).`,
          { icon: '⚠️', duration: 12000, style: { maxWidth: 520 } }
        );
      }
      refetchFiles();
      refetchStats();
      setSelectedFile(null);
      setShowMappingModal(false);
    },
    onError: (error) => {
      console.error('Sales data import failed:', error);
      toast.error(error.response?.data?.error || error.response?.data?.details || 'Sales data import failed');
    }
  });

  // Delete a sales-data file (and its records).
  const deleteFileMutation = useMutation({
    mutationFn: (id) => deleteSalesDataFile(id),
    onSuccess: (data) => {
      toast.success(data?.message || 'File deleted');
      refetchFiles();
      refetchStats();
    },
    onError: (e) => toast.error(e.response?.data?.error || 'Failed to delete file')
  });

  // Archive / restore a sales-data file (non-destructive; hides its records).
  const archiveMutation = useMutation({
    mutationFn: (id) => archiveSalesDataFile(id),
    onSuccess: () => { toast.success('File archived'); refetchFiles(); refetchStats(); },
    onError: (e) => toast.error(e.response?.data?.error || 'Failed to archive file')
  });
  const unarchiveMutation = useMutation({
    mutationFn: (id) => unarchiveSalesDataFile(id),
    onSuccess: () => { toast.success('File restored'); refetchFiles(); refetchStats(); },
    onError: (e) => toast.error(e.response?.data?.error || 'Failed to restore file')
  });

  const handleDeleteFile = (file) => {
    if (window.confirm(`Delete "${file.originalFileName}" and all its sales records? This cannot be undone.`)) {
      deleteFileMutation.mutate(file.id);
    }
  };

  // Download template mutation
  const downloadTemplateMutation = useMutation({
    mutationFn: downloadSalesDataTemplate,
    onSuccess: () => {
    },
    onError: (error) => {
      console.error('Template download failed:', error);
    }
  });

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      setSelectedFile(file);
    }
  };

  const handleImport = () => {
    if (!selectedFile) {
      alert('Please select a file to import');
      return;
    }

    importMutation.mutate({
      file: selectedFile,
      options: importOptions
    });
  };

  const handleMapColumns = () => {
    if (!selectedFile) { alert('Please select a file first'); return; }
    setShowMappingModal(true);
  };

  const handleImportWithMapping = (mapping) => {
    importMutation.mutate({ file: selectedFile, options: { ...importOptions, mapping } });
  };

  const handleDownloadTemplate = () => {
    downloadTemplateMutation.mutate();
  };

  const handleViewFile = (fileId) => {
    setViewingFileId(fileId);
    setShowFileModal(true);
  };

  const handleCloseModal = () => {
    setShowFileModal(false);
    setViewingFileId(null);
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value,
      page: 1 // Reset to first page when filtering
    }));
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'PROCESSED':
        return 'text-green-600 bg-green-100';
      case 'PROCESSING':
        return 'text-blue-600 bg-blue-100';
      case 'FAILED':
        return 'text-red-600 bg-red-100';
      case 'PARTIALLY_PROCESSED':
        return 'text-yellow-600 bg-yellow-100';
      default:
        return 'text-gray-600 bg-gray-100';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'PROCESSED':
        return CheckCircle;
      case 'PROCESSING':
        return RefreshCw;
      case 'FAILED':
        return XCircle;
      case 'PARTIALLY_PROCESSED':
        return AlertTriangle;
      default:
        return FileText;
    }
  };

  // Computed before the loading early-return so the sort hook always runs (rules of hooks).
  const files = filesData?.data?.files || [];
  const { sorted: sortedFiles, sort, toggleSort } = useTableSort(files, SALES_FILE_SORTS);

  if (filesLoading || statsLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  const pagination = filesData?.data?.pagination || {};
  const stats = statsData?.data || {};

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="mb-8">
        <div className="flex justify-between items-start">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Sales Data Management</h1>
            <p className="text-gray-600">
              Import and manage pharmaceutical industry standard sales data for chargeback validation
            </p>
          </div>
          <button
            onClick={() => setShowFileModal(true)}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 mr-3"
            title="Import sales data from CSV/Excel"
          >
            <Upload className="h-4 w-4 mr-2" />
            Import Sales Data
          </button>
          <button
            onClick={exportToExcel}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700"
            title="Export Excel"
          >
            <FileSpreadsheet className="h-4 w-4 mr-2" />
            Export Excel
          </button>
        </div>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="p-2 bg-blue-100 rounded-lg">
              <FileText className="h-6 w-6 text-blue-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Total Files</p>
              <p className="text-2xl font-bold text-gray-900">
                {stats.totalFiles?.toLocaleString() || 0}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="p-2 bg-green-100 rounded-lg">
              <CheckCircle className="h-6 w-6 text-green-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Total Records</p>
              <p className="text-2xl font-bold text-gray-900">
                {stats.totalRecords?.toLocaleString() || 0}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="p-2 bg-yellow-100 rounded-lg">
              <AlertTriangle className="h-6 w-6 text-yellow-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Validation Rate</p>
              <p className="text-2xl font-bold text-gray-900">
                {stats.validationRate?.toFixed(1) || 0}%
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="p-2 bg-purple-100 rounded-lg">
              <BarChart3 className="h-6 w-6 text-purple-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Unique Customers</p>
              <p className="text-2xl font-bold text-gray-900">
                {stats.uniqueCustomers?.toLocaleString() || 0}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Import Section */}
      <div className="bg-white rounded-lg shadow mb-8">
        <div className="p-6 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Import Sales Data</h2>
        </div>
        <div className="p-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* File Upload */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Select File
              </label>
              <div className="flex items-center space-x-4">
                <input
                  type="file"
                  accept=".csv,.xlsx,.xls"
                  onChange={handleFileSelect}
                  className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                />
                <button
                  onClick={handleDownloadTemplate}
                  disabled={downloadTemplateMutation.isPending}
                  className="flex items-center px-3 py-2 text-sm bg-gray-100 hover:bg-gray-200 rounded-md disabled:opacity-50"
                  title="Download Template"
                >
                  {downloadTemplateMutation.isPending ? (
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-600 mr-2"></div>
                  ) : (
                    <Download className="h-4 w-4 mr-2" />
                  )}
                  Template
                </button>
              </div>
              {selectedFile && (
                <p className="mt-2 text-sm text-gray-600">
                  Selected: {selectedFile.name} ({formatFileSize(selectedFile.size)})
                </p>
              )}
            </div>

            {/* Import Options */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Import Options
              </label>
              <div className="space-y-2">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={importOptions.validateData}
                    onChange={(e) => setImportOptions(prev => ({
                      ...prev,
                      validateData: e.target.checked
                    }))}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <span className="ml-2 text-sm text-gray-700">Validate Data</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={importOptions.detectDuplicates}
                    onChange={(e) => setImportOptions(prev => ({
                      ...prev,
                      detectDuplicates: e.target.checked
                    }))}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <span className="ml-2 text-sm text-gray-700">Detect Duplicates</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={importOptions.matchCustomers}
                    onChange={(e) => setImportOptions(prev => ({
                      ...prev,
                      matchCustomers: e.target.checked
                    }))}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <span className="ml-2 text-sm text-gray-700">Match Customers</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={importOptions.matchProducts}
                    onChange={(e) => setImportOptions(prev => ({
                      ...prev,
                      matchProducts: e.target.checked
                    }))}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <span className="ml-2 text-sm text-gray-700">Match Products</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={importOptions.matchContracts}
                    onChange={(e) => setImportOptions(prev => ({
                      ...prev,
                      matchContracts: e.target.checked
                    }))}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <span className="ml-2 text-sm text-gray-700">Match Contracts</span>
                </label>
              </div>
            </div>
          </div>

          <div className="mt-6 flex flex-wrap gap-3">
            <button
              onClick={handleMapColumns}
              disabled={!selectedFile || importMutation.isPending}
              className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              title="Preview and map the file's columns, then import"
            >
              <Upload className="h-4 w-4 mr-2" />
              Map Columns & Import
            </button>
            <button
              onClick={handleImport}
              disabled={!selectedFile || importMutation.isPending}
              className="flex items-center px-4 py-2 bg-white text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              title="Import immediately using automatic column detection"
            >
              {importMutation.isPending ? (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-500 mr-2"></div>
              ) : null}
              Quick import (auto-detect)
            </button>
          </div>
        </div>
      </div>

      <ColumnMappingModal
        file={selectedFile}
        isOpen={showMappingModal}
        onClose={() => setShowMappingModal(false)}
        onImport={handleImportWithMapping}
        importing={importMutation.isPending}
      />

      {/* Tabs: line-by-line records vs uploaded files */}
      <div className="mb-4">
        <PillTabs
          active={tab}
          onChange={setTab}
          tabs={[{ id: 'records', label: 'Line Items' }, { id: 'files', label: 'Uploaded Files' }]}
        />
      </div>

      {tab === 'records' && <SalesRecordsTable />}

      {/* Files List */}
      {tab === 'files' && (
      <div className="bg-white rounded-lg shadow">
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-gray-900">Sales Data Files</h2>
            <div className="flex items-center space-x-4">
              {/* Active / Archived toggle */}
              <div className="inline-flex rounded-lg border border-gray-200 bg-gray-50 p-0.5 text-sm">
                <button
                  onClick={() => handleFilterChange('archived', '')}
                  className={`rounded-md px-3 py-1 font-medium ${filters.archived !== 'true' ? 'bg-white text-primary-700 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
                >
                  Active
                </button>
                <button
                  onClick={() => handleFilterChange('archived', 'true')}
                  className={`rounded-md px-3 py-1 font-medium ${filters.archived === 'true' ? 'bg-white text-primary-700 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
                >
                  Archived
                </button>
              </div>
              <select
                value={filters.status}
                onChange={(e) => handleFilterChange('status', e.target.value)}
                className="px-3 py-1 border border-gray-300 rounded-md text-sm"
              >
                <option value="">All Status</option>
                <option value="UPLOADED">Uploaded</option>
                <option value="PROCESSING">Processing</option>
                <option value="PROCESSED">Processed</option>
                <option value="FAILED">Failed</option>
                <option value="PARTIALLY_PROCESSED">Partially Processed</option>
              </select>
              <button
                onClick={() => refetchFiles()}
                className="p-1 text-gray-500 hover:text-gray-700"
                title="Refresh"
              >
                <RefreshCw className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <SortTh label="File" sortKey="file" sort={sort} onSort={toggleSort} />
                <SortTh label="Status" sortKey="status" sort={sort} onSort={toggleSort} />
                <SortTh label="Records" sortKey="records" sort={sort} onSort={toggleSort} />
                <SortTh label="Upload Date" sortKey="uploaded" sort={sort} onSort={toggleSort} />
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {sortedFiles.map((file) => {
                const StatusIcon = getStatusIcon(file.status);
                return (
                  <tr key={file.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-medium text-gray-900 flex items-center gap-2">
                          {file.originalFileName}
                          {file.isArchived && (
                            <span className="inline-flex items-center gap-1 rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-500">
                              <Archive className="h-3 w-3" /> Archived
                            </span>
                          )}
                        </div>
                        <div className="text-sm text-gray-500">
                          {formatFileSize(file.fileSize)}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(file.status)}`}>
                        <StatusIcon className="h-3 w-3 mr-1" />
                        {file.status.replace('_', ' ')}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      <div>
                        <div>Total: {file.totalRecords?.toLocaleString() || 0}</div>
                        <div className="text-gray-500">
                          Processed: {file.processedRecords?.toLocaleString() || 0}
                        </div>
                        {file.errorRecords > 0 && (
                          <div className="text-red-500">
                            Errors: {file.errorRecords}
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatDate(file.uploadDate)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex items-center gap-3">
                        <button
                          onClick={() => handleViewFile(file.id)}
                          className="text-blue-600 hover:text-blue-900"
                          title="View file details"
                        >
                          <Eye className="h-4 w-4" />
                        </button>
                        {file.isArchived ? (
                          <button
                            onClick={() => unarchiveMutation.mutate(file.id)}
                            disabled={unarchiveMutation.isPending}
                            className="text-emerald-600 hover:text-emerald-800 disabled:opacity-50"
                            title="Restore this file to the active list"
                          >
                            <ArchiveRestore className="h-4 w-4" />
                          </button>
                        ) : (
                          <button
                            onClick={() => archiveMutation.mutate(file.id)}
                            disabled={archiveMutation.isPending}
                            className="text-gray-500 hover:text-gray-800 disabled:opacity-50"
                            title="Archive this file (hides its records; kept for audit)"
                          >
                            <Archive className="h-4 w-4" />
                          </button>
                        )}
                        <button
                          onClick={() => handleDeleteFile(file)}
                          disabled={deleteFileMutation.isPending}
                          className="text-red-600 hover:text-red-900 disabled:opacity-50"
                          title="Delete this file and all its records"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {pagination.pages > 1 && (
          <div className="px-6 py-3 border-t border-gray-200">
            <div className="flex items-center justify-between">
              <div className="text-sm text-gray-700">
                Showing {((pagination.page - 1) * pagination.limit) + 1} to {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total} results
              </div>
              <div className="flex space-x-2">
                <button
                  onClick={() => handleFilterChange('page', pagination.page - 1)}
                  disabled={pagination.page <= 1}
                  className="px-3 py-1 text-sm bg-gray-100 hover:bg-gray-200 rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
                  title="Previous page"
                >
                  Previous
                </button>
                <span className="px-3 py-1 text-sm bg-blue-100 text-blue-800 rounded-md">
                  {pagination.page}
                </span>
                <button
                  onClick={() => handleFilterChange('page', pagination.page + 1)}
                  disabled={pagination.page >= pagination.pages}
                  className="px-3 py-1 text-sm bg-gray-100 hover:bg-gray-200 rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
                  title="Next page"
                >
                  Next
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
      )}

      {/* Import Results */}
      {importMutation.data && (
        <div className="mt-8 bg-white rounded-lg shadow">
          <div className="p-6 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">Import Results</h2>
          </div>
          <div className="p-6">
            <div className="bg-green-50 border border-green-200 rounded-md p-4">
              <div className="flex">
                <CheckCircle className="h-5 w-5 text-green-400" />
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-green-800">
                    Import Completed Successfully
                  </h3>
                  <div className="mt-2 text-sm text-green-700">
                    <p>File ID: {importMutation.data.data.fileId}</p>
                    <p>Total Records: {importMutation.data.data.totalRecords}</p>
                    <p>Processed Records: {importMutation.data.data.processedRecords}</p>
                    {importMutation.data.data.errorRecords > 0 && (
                      <p>Error Records: {importMutation.data.data.errorRecords}</p>
                    )}
                    {importMutation.data.data.duplicates > 0 && (
                      <p>Duplicates: {importMutation.data.data.duplicates}</p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* File Detail Modal */}
      <SalesDataFileModal
        fileId={viewingFileId}
        isOpen={showFileModal}
        onClose={handleCloseModal}
      />
    </div>
  );
};

export default SalesDataDashboard;
